/**
 * SystemSchematic Component
 * Renders a vertical 'House Cross-Section' showing component locations
 */

import { Flame, Battery, Droplets, Database, Wind } from 'lucide-react';
import type { SystemLayout, SystemComponent as LegacySystemComponent, ComponentLocation } from './types';
import type { SystemVisualModel, SystemComponent, ComponentLocation as CoreComponentLocation } from '../../../../../src/core/types';

interface SystemSchematicProps {
  layout: SystemLayout | null;
  visualModel?: SystemVisualModel;
}

const LOCATION_ORDER: ComponentLocation[] = ['Loft', 'Airing Cupboard', 'Utility/Kitchen', 'External'];

// Map from core component locations to legacy UI locations
const CORE_LOCATION_TO_UI: Record<CoreComponentLocation, ComponentLocation> = {
  'loft': 'Loft',
  'airing_cupboard': 'Airing Cupboard',
  'kitchen': 'Utility/Kitchen',
  'utility_room': 'Utility/Kitchen',
  'external': 'External',
  'basement': 'Utility/Kitchen',
  'garage': 'External',
  'under_stairs': 'Utility/Kitchen',
  'bathroom': 'Airing Cupboard',
};

const LOCATION_COLORS: Record<ComponentLocation, string> = {
  'Loft': '#f1f5f9',
  'Airing Cupboard': '#e0f2fe',
  'Utility/Kitchen': '#fef3c7',
  'External': '#dcfce7',
};

// Emoji fallbacks for when SVG icons fail to render
const EMOJI_FALLBACKS: Record<string, string> = {
  'boiler': '🔥',
  'cylinder': '🔋',
  'tank': '💧',
  'heatPump': '🌬️',
  'heat_pump': '🌬️',
  'buffer': '💾',
};

/**
 * Get the appropriate icon for a component with fallback support
 */
function getComponentIcon(iconKey: string) {
  const iconProps = { size: 48, strokeWidth: 1.5 };
  
  try {
    switch (iconKey) {
      case 'boiler':
        return <Flame {...iconProps} color="#ef4444" />;
      case 'cylinder':
        return <Battery {...iconProps} color="#3b82f6" style={{ transform: 'rotate(90deg)' }} />;
      case 'tank':
        return <Droplets {...iconProps} color="#0ea5e9" />;
      case 'heatPump':
      case 'heat_pump':
        return <Wind {...iconProps} color="#10b981" />;
      case 'buffer':
        return <Database {...iconProps} color="#8b5cf6" />;
      default:
        return <Battery {...iconProps} />;
    }
  } catch (error) {
    // Fallback to emoji if SVG rendering fails
    console.warn('SVG icon rendering failed, using emoji fallback', error);
    const emoji = EMOJI_FALLBACKS[iconKey] || '📦';
    return (
      <span style={{ fontSize: '48px', lineHeight: 1 }} aria-label={iconKey}>
        {emoji}
      </span>
    );
  }
}

/**
 * Group components by location (for legacy layout)
 */
function groupComponentsByLocation(components: LegacySystemComponent[]) {
  const grouped: Record<ComponentLocation, LegacySystemComponent[]> = {
    'Loft': [],
    'Airing Cupboard': [],
    'Utility/Kitchen': [],
    'External': [],
  };
  
  components.forEach(component => {
    grouped[component.location].push(component);
  });
  
  return grouped;
}

/**
 * Group visual model components by UI location
 */
function groupVisualModelComponents(components: SystemComponent[]) {
  const grouped: Record<ComponentLocation, SystemComponent[]> = {
    'Loft': [],
    'Airing Cupboard': [],
    'Utility/Kitchen': [],
    'External': [],
  };
  
  components.forEach(component => {
    const uiLocation = CORE_LOCATION_TO_UI[component.location] || 'Utility/Kitchen';
    grouped[uiLocation].push(component);
  });
  
  return grouped;
}

export function SystemSchematic({ layout, visualModel }: SystemSchematicProps) {
  // Use visual model if available
  const groupedComponents = visualModel 
    ? groupVisualModelComponents(visualModel.topology.components)
    : layout
    ? groupComponentsByLocation(layout.components)
    : null;

  if (!groupedComponents) {
    return null;
  }

  // Get capacity info from visual model or layout
  const capacityInfo = visualModel
    ? {
        showers: visualModel.capacitySignals.simultaneousOutlets,
        baths: visualModel.capacitySignals.bathsWithoutWaiting,
        description: `${visualModel.capacitySignals.simultaneousOutlets} simultaneous outlet${visualModel.capacitySignals.simultaneousOutlets > 1 ? 's' : ''}`
      }
    : layout
    ? layout.capacity
    : null;
  
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: '8px',
      padding: '16px',
      border: '2px solid #cbd5e1',
      borderRadius: '8px',
      background: 'white'
    }}>
      <h3 style={{ 
        fontSize: '18px', 
        fontWeight: 600, 
        marginBottom: '8px',
        textAlign: 'center',
        color: '#1e293b'
      }}>
        System Layout
      </h3>
      
      {/* Render zones from top to bottom */}
      {LOCATION_ORDER.map(location => {
        const componentsInLocation = groupedComponents[location];
        
        // Skip empty locations
        if (componentsInLocation.length === 0) {
          return null;
        }
        
        return (
          <div
            key={location}
            style={{
              background: LOCATION_COLORS[location],
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              padding: '12px',
              minHeight: '80px',
            }}
          >
            {/* Location Header */}
            <div style={{ 
              fontSize: '12px', 
              fontWeight: 600, 
              color: '#64748b',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {location}
            </div>
            
            {/* Components in this location */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              justifyContent: 'center',
            }}>
              {componentsInLocation.map((component, idx) => (
                <div
                  key={`${component.label}-${idx}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px',
                    background: 'white',
                    borderRadius: '6px',
                    border: component.optional ? '2px dashed #94a3b8' : '1px solid #e2e8f0',
                    minWidth: '100px',
                    opacity: component.optional ? 0.7 : 1,
                  }}
                >
                  {/* Icon */}
                  <div>{getComponentIcon('iconKey' in component ? component.iconKey : component.type)}</div>
                  
                  {/* Label */}
                  <div style={{ 
                    fontSize: '11px', 
                    fontWeight: 500, 
                    textAlign: 'center',
                    color: '#334155'
                  }}>
                    {component.label}
                  </div>
                  
                  {/* Optional badge */}
                  {component.optional && (
                    <div style={{ 
                      fontSize: '9px', 
                      color: '#64748b',
                      fontStyle: 'italic'
                    }}>
                      (Optional)
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      
      {/* Capacity Section */}
      {capacityInfo && (
        <div style={{
          marginTop: '16px',
          padding: '12px',
          background: '#f8fafc',
          borderRadius: '6px',
          border: '1px solid #e2e8f0',
        }}>
          <div style={{ 
            fontSize: '12px', 
            fontWeight: 600, 
            color: '#64748b',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Hot Water Capacity
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '24px',
            marginBottom: '8px',
          }}>
            {/* Showers */}
            {capacityInfo.showers > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '24px' }}>🚿</span>
                <span style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                  × {capacityInfo.showers}
                </span>
              </div>
            )}
            
            {/* Baths */}
            {capacityInfo.baths > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '24px' }}>🛁</span>
                <span style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                  × {capacityInfo.baths}
                </span>
              </div>
            )}
          </div>
          
          <div style={{ 
            fontSize: '11px', 
            color: '#64748b',
            textAlign: 'center',
            fontStyle: 'italic'
          }}>
            {capacityInfo.description}
          </div>
        </div>
      )}
    </div>
  );
}
